import { CloseIcon, SearchIcon } from "@src/assets";
import { Navigation, PageLayout, TextInput } from "@src/components";
import { useSwapContext } from "@src/contexts";
import { Chain } from "@src/models";
import { includesIgnoreCase } from "@src/utils";
import { forwardRef, HTMLAttributes, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Virtuoso } from "react-virtuoso";
import { UsdcChainItem } from "./UsdcChainItem";
import { CASH_TOKEN_ADDRESS_BY_CHAIN } from "@src/config";
import { RoutePath } from "@src/interfaces";

export const UsdcChainPickerView = () => {
  const tokenSearchRef = useRef(null);
  const [searchValue, setSearchValue] = useState("");
  const location = useLocation();
  const { type } = location.state || {};

  const navigate = useNavigate();
  const {
    chain: selectedChain,
    setChain: setSelectedChain,
    cashBridgeChain,
    setCashBridgeChain,
    allTokens,
    supportedChainsMap,
    tokensBalances,
  } = useSwapContext();

  const usdcTokens = useMemo(
    () =>
      allTokens.filter((t) => {
        const usdcAddressees = Object.values(CASH_TOKEN_ADDRESS_BY_CHAIN);

        return usdcAddressees.includes(t.address);
      }),
    [allTokens],
  );

  const usdcTokensWithChains = useMemo(
    () =>
      usdcTokens
        .map((t) => {
          return { ...t, chain: supportedChainsMap[t.chainId] };
        })
        .filter((t) => t.chain),
    [usdcTokens, supportedChainsMap],
  );

  const filteredTokens = useMemo(() => {
    const withoutSelected = usdcTokensWithChains.filter(
      (token) =>
        (type === "destination" && token.chainId !== selectedChain?.chainId) ||
        type === "source",
    );

    return withoutSelected.filter(
      (token) =>
        includesIgnoreCase(token.chainId, searchValue) ||
        includesIgnoreCase(token.name, searchValue) ||
        includesIgnoreCase(token.chain?.name, searchValue),
    );
  }, [usdcTokensWithChains, searchValue, selectedChain?.chainId, type]);

  const handleSwitchChain = (chain: Chain) => {
    if (type === "source") {
      setSelectedChain(chain);

      if (chain.chainId === cashBridgeChain?.chainId) {
        setCashBridgeChain(undefined);
      }
    } else {
      setCashBridgeChain(chain);

      if (selectedChain?.chainId === chain.chainId) {
        setSelectedChain(undefined);
      }
    }

    navigate(-1);
  };

  return (
    <PageLayout
      navigation={
        <Navigation
          leftItems={[
            {
              content: "Pick chain",
              disabled: true,
              path: RoutePath.PickChain,
            },
          ]}
          rightItems={[
            {
              content: <CloseIcon />,
              onClick: () => navigate(-1),
            },
          ]}
        />
      }
    >
      <div className="flex flex-col gap-4 h-full w-full max-w-full">
        <div className="bg-card_bg rounded-xl px-4">
          <TextInput
            ref={tokenSearchRef}
            className="h-10"
            inputIcon={
              <div className="fill-text_primary">
                <SearchIcon />
              </div>
            }
            placeholder="Search network"
            value={searchValue}
            handleChange={setSearchValue}
          />
        </div>

        <div className="flex flex-col h-full w-full max-w-full !overflow-visible">
          {filteredTokens.length > 0 ? (
            <Virtuoso
              className="h-full w-full max-w-full !overflow-visible"
              data={filteredTokens}
              itemContent={(_, token) => {
                const currentChain =
                  type === "source" ? selectedChain : cashBridgeChain;

                return (
                  token && (
                    <UsdcChainItem
                      token={token}
                      chain={token.chain!}
                      isSelected={token.chainId === currentChain?.chainId}
                      balances={
                        token?.chainId
                          ? tokensBalances[token?.chainId]?.[token.address]
                          : undefined
                      }
                      onClick={() => {
                        handleSwitchChain(supportedChainsMap[token.chainId]!);
                      }}
                    />
                  )
                );
              }}
              components={{
                List: forwardRef<
                  HTMLDivElement,
                  HTMLAttributes<HTMLDivElement>
                >(({ style, ...props }, ref) => (
                  <div
                    ref={ref}
                    style={{
                      ...style,
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                    {...props}
                  />
                )),
              }}
            />
          ) : (
            <div className="flex flex-col h-full w-full items-center">
              <p className="text-text_primary text-opacity-60">
                No results available.
              </p>
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
};
